import { ChangeDetectionStrategy, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, tap, takeUntil } from 'rxjs/operators';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { InfoCellComponent } from '../../../rx-intuitive-patient-header/components/info-cell/info-cell.component';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { CheckmarkEmptyIconComponent } from '@itero/ui-toolkit-angular/icons';
import { RxIntuitiveDoctorLicenseFacade } from '../../rx-intuitive-doctor-license.facade';

@Component({
	selector: 'rx-intuitive-doctor-license',
	templateUrl: './rx-intuitive-doctor-license.component.html',
	styleUrls: ['./rx-intuitive-doctor-license.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NgIf,
		AsyncPipe,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		InfoCellComponent,
		TlkButtonComponent,
		CheckmarkEmptyIconComponent
	],
	providers: [RxIntuitiveDoctorLicenseFacade],
	standalone: true
})
export class RxIntuitiveDoctorLicenseComponent extends BaseDestroyableComponent implements OnInit {
	private readonly facade = inject(RxIntuitiveDoctorLicenseFacade);
	private readonly translate = inject(TranslateService);

	doctorInfo$ = this.facade.doctorInfo$;
	isLicenseEditable$ = this.facade.isLicenseEditable$;
	showLicenseReadOnly$ = this.facade.isLicenseEditable$.pipe(map(editable => !editable));
	licenseFormControl = new FormControl<string | null>(null, [
		control => Validators.required(control),
		control => Validators.maxLength(36)(control)
	]);
	isLicenseInputFocused = false;

	@ViewChild('licenseInput') licenseInputRef?: ElementRef<HTMLInputElement>;

	get showRequiredPlaceholder(): boolean {
		return this.licenseFormControl.invalid && this.licenseFormControl.touched && !this.licenseFormControl.hasError('maxlength');
	}

	get licenseInputPlaceholder(): string {
		if (this.showRequiredPlaceholder && !this.licenseFormControl.value?.trim()) {
			return '';
		}

		return this.translate.instant('DoctorSection.AddLicenseNumber');
	}

	get licenseErrorMessage(): string {
		return this.licenseFormControl.hasError('maxlength')
			? this.translate.instant('DoctorSection.LicenseInvalidLength')
			: this.translate.instant('DoctorSection.LicenseIsRequired');
	}

	getTreatedByTitle(doctorInfo: string): string {
		return `${this.translate.instant('IntuitivePatient.TreatedBy')} ${doctorInfo}`;
	}

	ngOnInit(): void {
		this.facade.license$
			.pipe(
				tap(license => this.licenseFormControl.patchValue(license ?? null, { emitEvent: false })),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	saveLicense(): void {
		const value = this.licenseFormControl.value?.trim() ?? '';

		this.licenseFormControl.markAllAsTouched();
		if (this.licenseFormControl.invalid) {
			this.licenseInputRef?.nativeElement?.blur();
		}
		if (this.licenseFormControl.valid) {
			this.facade.updateLicense({ license: value });
		}
	}
}
